import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import ConvertToProposalModal from "../components/rfp/ConvertToProposalModal";
import RFPChatPanel from "../components/rfp/RFPChatPanel";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";

// Helper function to parse date strings without timezone issues
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('T')[0].split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const RFPAnalysisDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();

  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [extracting, setExtracting] = useState(null); // 'requirements' | 'deliverables' | 'proposalReqs'
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showDetailedRfpSummary, setShowDetailedRfpSummary] = useState(false);
  const [showDetailedFitAnalysis, setShowDetailedFitAnalysis] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAnalysis = async () => {
      await loadResearchDetails();

      // Check if we need to start analysis (only if still mounted)
      if (
        isMounted &&
        location.state?.startAnalysis &&
        location.state?.contextDocIds
      ) {
        startAnalysis(location.state.contextDocIds, location.state.selectedModel);

        // Clear the state so it doesn't trigger again on re-render
        window.history.replaceState({}, document.title);
      }
    };

    initializeAnalysis();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
    }
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiClient.getUsers();
      if (response.success) {
        setUsers(response.data.users || []);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  const loadResearchDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getRFPResearch(id);

      if (response.success) {
        setResearch(response.data);
      }
    } catch (err) {
      setError("Failed to load research details");
      console.error("Error loading research:", err);
    } finally {
      setLoading(false);
    }
  };

  const startAnalysis = async (contextDocIds, selectedModel = null) => {
    // Prevent duplicate calls
    if (analyzing) {
      console.log("Analysis already in progress, skipping duplicate call");
      return;
    }

    try {
      setAnalyzing(true);
      setError("");

      const response = await apiClient.analyzeRFP(id, contextDocIds, selectedModel);

      if (response.success) {
        // Reload to show analysis results
        await loadResearchDetails();
      }
    } catch (err) {
      setError(
        "Analysis failed: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiClient.updateRFPResearchStatus(id, newStatus);
      setResearch((prev) => ({
        ...prev,
        research: { ...prev.research, status: newStatus },
      }));
    } catch (err) {
      setError("Failed to update status");
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleConvertToProposal = async () => {
    try {
      setShowConvertModal(false);
      setLoading(true);

      console.log("Converting with selectedUsers:", selectedUsers);
      const response = await apiClient.convertRFPToProposal(id, {
        selectedUsers,
      });

      if (response.success) {
        // Navigate to the new proposal
        navigate(`/workspace/${response.data.proposalId}`);
      }
    } catch (err) {
      console.error("Conversion error:", err);
      console.error("Error details:", err.response?.data);
      console.error("Validation errors:", err.response?.data?.errors);
      const errorMsg =
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.message ||
        "Failed to convert to proposal";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this research entry? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      await apiClient.deleteRFPResearch(id);
      navigate("/rfp-research");
    } catch (err) {
      setError("Failed to delete research entry");
    }
  };

  const handleExtractRequirements = async () => {
    // Prevent duplicate calls
    if (extracting) {
      console.log("Extraction already in progress, skipping duplicate call");
      return;
    }

    try {
      setExtracting("requirements");
      setError("");
      const response = await apiClient.extractRFPRequirements(id);
      if (response.success) {
        await loadResearchDetails(); // Reload to show extracted data
      }
    } catch (err) {
      setError(
        "Failed to extract requirements: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setExtracting(null);
    }
  };

  const handleExtractDeliverables = async () => {
    // Prevent duplicate calls
    if (extracting) {
      console.log("Extraction already in progress, skipping duplicate call");
      return;
    }

    try {
      setExtracting("deliverables");
      setError("");
      const response = await apiClient.extractRFPDeliverables(id);
      if (response.success) {
        await loadResearchDetails();
      }
    } catch (err) {
      setError(
        "Failed to extract deliverables: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setExtracting(null);
    }
  };

  const handleExtractProposalReqs = async () => {
    // Prevent duplicate calls
    if (extracting) {
      console.log("Extraction already in progress, skipping duplicate call");
      return;
    }

    try {
      setExtracting("proposalReqs");
      setError("");
      const response = await apiClient.extractRFPProposalRequirements(id);
      if (response.success) {
        await loadResearchDetails();
      }
    } catch (err) {
      setError(
        "Failed to extract proposal requirements: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setExtracting(null);
    }
  };

  const handleDownloadAnalysis = async () => {
    if (downloading) {
      return;
    }

    try {
      setDownloading(true);
      setError("");
      await apiClient.downloadSingleRFPResearch(id);
    } catch (err) {
      setError(
        "Failed to download analysis: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="RFP Analysis">
        <div className="py-12 text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-slate-600">Loading analysis...</p>
        </div>
      </Layout>
    );
  }

  if (!research) {
    return (
      <Layout title="RFP Analysis">
        <ErrorMessage message="Research entry not found" />
      </Layout>
    );
  }

  const { research: researchData } = research;
  const {
    bidRecommendation,
    requirements = [],
    deliverables = [],
    evaluationCriteria,
    rfpDetails,
    response_difficulty_score,
    response_difficulty_summary,
  } = research.research;

  const getStatusBadge = (status) => {
    const badges = {
      analyzing: "bg-blue-100 text-blue-800",
      analyzed: "bg-gray-100 text-gray-800",
      bid: "bg-green-100 text-green-800",
      no_bid: "bg-red-100 text-red-800",
      converted: "bg-purple-100 text-purple-800",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status]}`}
      >
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const getRiskBadge = (severity) => {
    const badges = {
      Low: "bg-green-50 text-green-700 border-green-200",
      Medium: "bg-amber-50 text-amber-700 border-amber-200",
      High: "bg-red-50 text-red-700 border-red-200",
    };
    return badges[severity] || badges.Medium;
  };

  return (
    <Layout title="RFP Analysis">
      <div className="flex gap-6 h-[calc(100vh-100px)] mb-0">
        {/* Left Sidebar - Sticky */}
        <div className="w-1/3 flex-shrink-0 sticky top-6 h-full">
          <div className="flex flex-col h-full space-y-4">
            {/* Minimal Header */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex-shrink-0">
              <div className="flex items-start gap-3 mb-3">
                <button
                  onClick={() => navigate("/rfp-research")}
                  className="text-slate-400 hover:text-slate-600 mt-1"
                >
                  <FontAwesomeIcon icon="arrow-left" />
                </button>
                <h1 className="text-xl font-bold text-slate-900 flex-1">
                  {researchData.rfp_title}
                </h1>
              </div>

              {researchData.customer_name && (
                <p className="text-base text-slate-700 mb-3">
                  <FontAwesomeIcon icon="building" className="mr-2" />
                  {researchData.customer_name}
                </p>
              )}

              {/* Info */}
              <div className="flex gap-4 text-sm text-slate-600">
                {researchData.rfp_number && (
                  <div>
                    <span className="font-semibold">RFP #:</span>{" "}
                    {researchData.rfp_number}
                  </div>
                )}
                {researchData.due_date && (
                  <div>
                    <span className="font-semibold">Due Date:</span>{" "}
                    {parseLocalDate(researchData.due_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Chat Panel - Takes remaining height */}
            <div className="flex-1 min-h-0">
              <RFPChatPanel researchId={id} />
            </div>
          </div>
        </div>

        {/* Right Column - Main Content (2/3 width) */}
        <div className="flex-1 space-y-6 overflow-y-auto">
          {/* Status and Action Controls - Single Row */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3">
            <div className="flex items-center gap-3">
              {/* Status Selector */}
              <select
                value={researchData.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="analyzing">Analyzing</option>
                <option value="analyzed">Analyzed</option>
                <option value="bid">Bid</option>
                <option value="no_bid">No Bid</option>
                <option value="converted">Converted</option>
              </select>

              {/* Action Buttons - In a Row */}
              <div className="flex items-center gap-2 flex-1 justify-end">
                <button
                  onClick={handleExtractRequirements}
                  disabled={extracting || researchData.requirements_extracted}
                  className={`px-3 py-2 rounded text-xs font-medium ${
                    researchData.requirements_extracted
                      ? "bg-green-100 text-green-700 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  }`}
                >
                  {researchData.requirements_extracted ? (
                    <>
                      <FontAwesomeIcon icon="check-circle" className="mr-1" />
                      Requirements ✓
                    </>
                  ) : extracting === "requirements" ? (
                    <>
                      <FontAwesomeIcon
                        icon="spinner"
                        className="mr-1 fa-spin"
                      />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="list" className="mr-1" />
                      Extract Requirements
                    </>
                  )}
                </button>

                <button
                  onClick={handleExtractDeliverables}
                  disabled={extracting || researchData.deliverables_extracted}
                  className={`px-3 py-2 rounded text-xs font-medium ${
                    researchData.deliverables_extracted
                      ? "bg-green-100 text-green-700 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  }`}
                >
                  {researchData.deliverables_extracted ? (
                    <>
                      <FontAwesomeIcon icon="check-circle" className="mr-1" />
                      Deliverables ✓
                    </>
                  ) : extracting === "deliverables" ? (
                    <>
                      <FontAwesomeIcon
                        icon="spinner"
                        className="mr-1 fa-spin"
                      />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="box" className="mr-1" />
                      Extract Deliverables
                    </>
                  )}
                </button>

                <button
                  onClick={handleExtractProposalReqs}
                  disabled={extracting || researchData.proposal_reqs_extracted}
                  className={`px-3 py-2 rounded text-xs font-medium ${
                    researchData.proposal_reqs_extracted
                      ? "bg-green-100 text-green-700 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  }`}
                >
                  {researchData.proposal_reqs_extracted ? (
                    <>
                      <FontAwesomeIcon icon="check-circle" className="mr-1" />
                      Proposal Reqs ✓
                    </>
                  ) : extracting === "proposalReqs" ? (
                    <>
                      <FontAwesomeIcon
                        icon="spinner"
                        className="mr-1 fa-spin"
                      />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="file-alt" className="mr-1" />
                      Extract Proposal Reqs
                    </>
                  )}
                </button>

                <button
                  onClick={handleDownloadAnalysis}
                  disabled={downloading}
                  className="px-3 py-2 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                  title="Download this analysis as Word document"
                >
                  {downloading ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="mr-1 fa-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="download" className="mr-1" />
                      Download
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowConvertModal(true)}
                  disabled={researchData.converted_to_proposal_id}
                  className={`px-3 py-2 rounded text-xs font-medium ${
                    researchData.converted_to_proposal_id
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  <FontAwesomeIcon icon="rocket" className="mr-1" />
                  {researchData.converted_to_proposal_id
                    ? "Converted"
                    : "Convert to Proposal"}
                </button>
              </div>

              {/* Additional Info */}
              <div className="flex items-center gap-3 ml-auto">
                {researchData.estimated_value && (
                  <span className="text-sm text-slate-600">
                    <FontAwesomeIcon icon="dollar-sign" className="mr-1" />$
                    {(researchData.estimated_value / 1000000).toFixed(1)}M
                  </span>
                )}
                {researchData.rfp_source_url && (
                  <a
                    href={researchData.rfp_source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 text-sm"
                  >
                    <FontAwesomeIcon
                      icon="external-link-alt"
                      className="mr-1"
                    />
                    View Source
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && <ErrorMessage message={error} />}

          {/* Analyzing State */}
          {analyzing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-blue-800 font-medium">Analyzing RFP...</p>
              <p className="text-sm text-blue-600">
                This may take 30-90 seconds
              </p>
            </div>
          )}

          {/* Executive Summary */}
          {researchData.executive_summary && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Executive Summary
              </h2>
              <p className="text-slate-700 leading-relaxed">
                {researchData.executive_summary}
              </p>
            </div>
          )}

          {/* RFP Summary */}
          {researchData.rfp_summary_text && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon="list-alt"
                    className="mr-2 text-blue-500"
                  />
                  RFP Summary
                </div>
                {researchData.rfp_summary_detailed && (
                  <button
                    onClick={() =>
                      setShowDetailedRfpSummary(!showDetailedRfpSummary)
                    }
                    className="text-sm text-primary-600 hover:text-primary-800 flex items-center space-x-1"
                  >
                    <FontAwesomeIcon
                      icon={
                        showDetailedRfpSummary ? "chevron-up" : "chevron-down"
                      }
                    />
                    <span>
                      {showDetailedRfpSummary ? "Show Brief" : "Show Detailed"}
                    </span>
                  </button>
                )}
              </h2>
              <div className="prose max-w-none">
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {showDetailedRfpSummary
                    ? researchData.rfp_summary_detailed
                    : researchData.rfp_summary_text}
                </p>
              </div>
            </div>
          )}

          {/* Company Fit Analysis */}
          {researchData.company_fit_analysis && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon="chart-line"
                    className="mr-2 text-green-500"
                  />
                  Company Fit Analysis
                </div>
                {researchData.company_fit_detailed && (
                  <button
                    onClick={() =>
                      setShowDetailedFitAnalysis(!showDetailedFitAnalysis)
                    }
                    className="text-sm text-primary-600 hover:text-primary-800 flex items-center space-x-1"
                  >
                    <FontAwesomeIcon
                      icon={
                        showDetailedFitAnalysis ? "chevron-up" : "chevron-down"
                      }
                    />
                    <span>
                      {showDetailedFitAnalysis ? "Show Brief" : "Show Detailed"}
                    </span>
                  </button>
                )}
              </h2>
              <div className="prose max-w-none">
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {showDetailedFitAnalysis
                    ? researchData.company_fit_detailed
                    : researchData.company_fit_analysis}
                </p>
              </div>
            </div>
          )}

          {/* Past Performance Requirements */}
          {researchData.past_performance_requirements && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Past Performance Requirements
              </h2>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">
                {researchData.past_performance_requirements}
              </div>
            </div>
          )}

          {/* Key Personnel Requirements */}
          {researchData.key_personnel_requirements && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Key Personnel Requirements
              </h2>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">
                {researchData.key_personnel_requirements}
              </div>
            </div>
          )}

          {/* Special Certifications & Submission Requirements */}
          {(research.research.specialCertifications?.length > 0 ||
            research.research.submissionRequirements?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Special Certifications */}
              {research.research.specialCertifications &&
                research.research.specialCertifications.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                      <FontAwesomeIcon
                        icon="certificate"
                        className="mr-2 text-amber-500"
                      />
                      Required Certifications
                    </h2>
                    <div className="space-y-3">
                      {research.research.specialCertifications.map(
                        (cert, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border ${
                              cert.companyHas
                                ? "bg-green-50 border-green-200"
                                : "bg-red-50 border-red-200"
                            }`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-medium text-slate-900">
                                {cert.name}
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded ${
                                  cert.companyHas
                                    ? "bg-green-200 text-green-800"
                                    : "bg-red-200 text-red-800"
                                }`}
                              >
                                {cert.companyHas ? "Have" : "Missing"}
                              </span>
                            </div>
                            {cert.impact && (
                              <div className="text-xs text-slate-600 mb-1">
                                Impact:{" "}
                                <span className="font-medium">
                                  {cert.impact}
                                </span>
                              </div>
                            )}
                            {cert.notes && (
                              <p className="text-sm text-slate-700 mt-1">
                                {cert.notes}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Submission Requirements */}
              {research.research.submissionRequirements &&
                research.research.submissionRequirements.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                      <FontAwesomeIcon
                        icon="clipboard-list"
                        className="mr-2 text-blue-500"
                      />
                      Required Documents for Submission
                    </h2>
                    <div className="space-y-2">
                      {research.research.submissionRequirements.map(
                        (req, idx) => (
                          <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-medium text-slate-900">
                                {req.documentType}
                              </span>
                              {req.mandatory && (
                                <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                                  Required
                                </span>
                              )}
                            </div>
                            {req.description && (
                              <p className="text-sm text-slate-700 mb-1">
                                {req.description}
                              </p>
                            )}
                            {req.format && (
                              <p className="text-xs text-slate-500">
                                Format: {req.format}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Bid Recommendation */}
          {bidRecommendation && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Bid Recommendation
              </h2>

              <div className="flex items-center space-x-4 mb-6">
                <div
                  className={`px-6 py-3 rounded-lg text-lg font-bold ${
                    bidRecommendation.decision === "bid"
                      ? "bg-green-500 text-white"
                      : bidRecommendation.decision === "no_bid"
                      ? "bg-red-500 text-white"
                      : "bg-amber-500 text-white"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={
                      bidRecommendation.decision === "bid"
                        ? "check-circle"
                        : bidRecommendation.decision === "no_bid"
                        ? "times-circle"
                        : "question-circle"
                    }
                    className="mr-2"
                  />
                  {bidRecommendation.decision.replace("_", " ").toUpperCase()}
                </div>

                {bidRecommendation.overallScore && (
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">
                        Overall Match Score
                      </span>
                      <span className="text-lg font-bold text-slate-900">
                        {bidRecommendation.overallScore}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${
                          bidRecommendation.overallScore >= 80
                            ? "bg-green-500"
                            : bidRecommendation.overallScore >= 60
                            ? "bg-amber-500"
                            : "bg-red-500"
                        }`}
                        style={{ width: `${bidRecommendation.overallScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Reasoning */}
              {bidRecommendation.reasoning &&
                bidRecommendation.reasoning.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-slate-900 mb-3">
                      Key Reasons:
                    </h3>
                    <ul className="space-y-2">
                      {bidRecommendation.reasoning.map((reason, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <FontAwesomeIcon
                            icon="check"
                            className="text-green-500 mt-1"
                          />
                          <span className="text-slate-700">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Strengths */}
                {bidRecommendation.strengths &&
                  bidRecommendation.strengths.length > 0 && (
                    <div>
                      <h3 className="font-medium text-green-700 mb-3 flex items-center">
                        <FontAwesomeIcon icon="star" className="mr-2" />
                        Competitive Strengths
                      </h3>
                      <ul className="space-y-2">
                        {bidRecommendation.strengths.map((strength, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-slate-700 bg-green-50 p-2 rounded"
                          >
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Gaps */}
                {bidRecommendation.criticalGaps &&
                  bidRecommendation.criticalGaps.length > 0 && (
                    <div>
                      <h3 className="font-medium text-red-700 mb-3 flex items-center">
                        <FontAwesomeIcon
                          icon="exclamation-triangle"
                          className="mr-2"
                        />
                        Critical Gaps
                      </h3>
                      <ul className="space-y-2">
                        {bidRecommendation.criticalGaps.map((gap, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-slate-700 bg-red-50 p-2 rounded"
                          >
                            {gap}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>

              {/* Risks */}
              {bidRecommendation.risks &&
                bidRecommendation.risks.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-medium text-slate-900 mb-3">
                      Risk Assessment
                    </h3>
                    <div className="space-y-3">
                      {bidRecommendation.risks.map((risk, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${getRiskBadge(
                            risk.severity
                          )}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="font-medium">{risk.risk}</span>
                            <span className="text-xs px-2 py-1 rounded bg-white border">
                              {risk.severity} Risk
                            </span>
                          </div>
                          {risk.mitigation && (
                            <p className="text-sm mt-2">
                              <strong>Mitigation:</strong> {risk.mitigation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Response Difficulty */}
          {response_difficulty_score && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Response Difficulty
              </h2>

              <div className="flex items-center space-x-4 mb-4">
                <div className={`px-6 py-3 rounded-lg text-lg font-bold ${
                  response_difficulty_score <= 3 ? 'bg-green-500 text-white' :
                  response_difficulty_score <= 6 ? 'bg-yellow-500 text-white' :
                  response_difficulty_score <= 8 ? 'bg-orange-500 text-white' :
                  'bg-red-500 text-white'
                }`}>
                  <FontAwesomeIcon icon="file-alt" className="mr-2" />
                  {response_difficulty_score}/10 - {
                    response_difficulty_score <= 3 ? 'Simple' :
                    response_difficulty_score <= 6 ? 'Moderate' :
                    response_difficulty_score <= 8 ? 'Complex' :
                    'Very Complex'
                  }
                </div>
              </div>

              {response_difficulty_summary && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-700">
                    {response_difficulty_summary}
                  </p>
                </div>
              )}

              <div className="mt-4 text-sm text-slate-600">
                <p>
                  <strong>What this means:</strong> This score reflects how much effort will be required to prepare and submit a response to this RFP,
                  including documentation requirements, certifications, past performance references, compliance matrices, and submission complexity.
                </p>
              </div>
            </div>
          )}

          {/* Red Flags Section */}
          {researchData.red_flags_json &&
            JSON.parse(researchData.red_flags_json).length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    className="text-red-600 text-2xl mt-1"
                  />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-red-900 mb-3">
                      ⚠️ Potential Disqualifiers / Red Flags
                    </h3>
                    <div className="space-y-3">
                      {JSON.parse(researchData.red_flags_json).map(
                        (flag, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-red-200 rounded p-4"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-red-900">
                                {flag.flag}
                              </h4>
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  flag.severity === "Critical"
                                    ? "bg-red-600 text-white"
                                    : flag.severity === "High"
                                    ? "bg-orange-500 text-white"
                                    : "bg-yellow-500 text-white"
                                }`}
                              >
                                {flag.severity}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">
                              {flag.description}
                            </p>
                            {flag.foundIn && (
                              <div className="flex items-center text-xs text-gray-500 mb-2">
                                <FontAwesomeIcon icon="file" className="mr-2" />
                                <span className="italic">{flag.foundIn}</span>
                              </div>
                            )}
                            <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              {flag.category}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Evaluation Criteria */}
          {evaluationCriteria &&
            evaluationCriteria.factors &&
            evaluationCriteria.factors.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">
                  Evaluation Criteria
                </h2>
                {evaluationCriteria.summary && (
                  <p className="text-slate-700 mb-4">
                    {evaluationCriteria.summary}
                  </p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                          Factor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                          Weight
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {evaluationCriteria.factors.map((factor, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">
                            {factor.factor}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {factor.weight}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {factor.description || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Requirements */}
          {requirements.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Key Requirements
              </h2>
              <div className="space-y-3">
                {requirements.map((req, idx) => (
                  <div
                    key={idx}
                    className="border-l-4 border-blue-500 pl-4 py-2 bg-slate-50 rounded-r"
                  >
                    <div className="flex items-start justify-between">
                      <p className="text-sm text-slate-700 flex-1">
                        {req.text}
                      </p>
                      <div className="ml-4 flex items-center space-x-2">
                        <span className="px-2 py-1 rounded text-xs bg-slate-200 text-slate-700 whitespace-nowrap">
                          {req.category}
                        </span>
                        {req.mandatory && (
                          <span className="text-xs text-red-600 font-medium whitespace-nowrap">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deliverables */}
          {deliverables.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Deliverables
              </h2>
              <div className="space-y-4">
                {deliverables.map((deliverable, idx) => (
                  <div
                    key={idx}
                    className="border-l-4 border-primary-500 pl-4 py-2"
                  >
                    <h4 className="font-medium text-slate-900">
                      {deliverable.name}
                    </h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {deliverable.description}
                    </p>
                    {deliverable.timeline && (
                      <p className="text-sm text-slate-500 mt-1">
                        <FontAwesomeIcon icon="clock" className="mr-1" />
                        {deliverable.timeline}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proposal Submission Requirements */}
          {researchData.proposal_requirements_json && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                <FontAwesomeIcon
                  icon="file-alt"
                  className="mr-2 text-blue-600"
                />
                Proposal Submission Requirements
              </h2>
              {(() => {
                const proposalReqs = JSON.parse(
                  researchData.proposal_requirements_json
                );
                return (
                  <div className="space-y-6">
                    {/* Proposal Structure */}
                    {proposalReqs.proposalStructure && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">
                          Proposal Structure
                        </h3>
                        {proposalReqs.proposalStructure.summary && (
                          <p className="text-sm text-slate-700 mb-3">
                            {proposalReqs.proposalStructure.summary}
                          </p>
                        )}
                        {proposalReqs.proposalStructure.volumesSections &&
                          proposalReqs.proposalStructure.volumesSections
                            .length > 0 && (
                            <div className="space-y-2">
                              {proposalReqs.proposalStructure.volumesSections.map(
                                (section, idx) => (
                                  <div
                                    key={idx}
                                    className="bg-slate-50 p-3 rounded"
                                  >
                                    <div className="flex items-start justify-between">
                                      <h4 className="font-medium text-slate-900">
                                        {section.name}
                                      </h4>
                                      {section.pageLimit && (
                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                          {section.pageLimit} pages
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-600 mt-1">
                                      {section.description}
                                    </p>
                                    {section.required && (
                                      <span className="text-xs text-red-600 font-medium mt-1 inline-block">
                                        * Required
                                      </span>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    )}

                    {/* Required Documents */}
                    {proposalReqs.requiredDocuments &&
                      proposalReqs.requiredDocuments.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2">
                            Required Documents
                          </h3>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                                    Document
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                                    Description
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                                    Format
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {proposalReqs.requiredDocuments.map(
                                  (doc, idx) => (
                                    <tr key={idx}>
                                      <td className="px-3 py-2 font-medium text-slate-900">
                                        {doc.documentType}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">
                                        {doc.description}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">
                                        {doc.format || "-"}
                                      </td>
                                      <td className="px-3 py-2">
                                        {doc.mandatory ? (
                                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                            Required
                                          </span>
                                        ) : (
                                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                            Optional
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {/* Format Requirements */}
                    {proposalReqs.formatRequirements && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">
                          Format Requirements
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {proposalReqs.formatRequirements.fileFormat && (
                            <div>
                              <span className="font-medium text-slate-700">
                                File Format:
                              </span>
                              <span className="ml-2 text-slate-600">
                                {proposalReqs.formatRequirements.fileFormat}
                              </span>
                            </div>
                          )}
                          {proposalReqs.formatRequirements.fontSize && (
                            <div>
                              <span className="font-medium text-slate-700">
                                Font Size:
                              </span>
                              <span className="ml-2 text-slate-600">
                                {proposalReqs.formatRequirements.fontSize}
                              </span>
                            </div>
                          )}
                          {proposalReqs.formatRequirements.margins && (
                            <div>
                              <span className="font-medium text-slate-700">
                                Margins:
                              </span>
                              <span className="ml-2 text-slate-600">
                                {proposalReqs.formatRequirements.margins}
                              </span>
                            </div>
                          )}
                        </div>
                        {proposalReqs.formatRequirements.other &&
                          proposalReqs.formatRequirements.other.length > 0 && (
                            <ul className="mt-2 space-y-1 text-sm text-slate-600">
                              {proposalReqs.formatRequirements.other.map(
                                (req, idx) => (
                                  <li key={idx}>• {req}</li>
                                )
                              )}
                            </ul>
                          )}
                      </div>
                    )}

                    {/* Submission Method */}
                    {proposalReqs.submissionMethod && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-4">
                        <h3 className="font-semibold text-blue-900 mb-2">
                          Submission Instructions
                        </h3>
                        <div className="space-y-2 text-sm">
                          {proposalReqs.submissionMethod.method && (
                            <div>
                              <span className="font-medium text-blue-900">
                                Method:
                              </span>
                              <span className="ml-2 text-blue-800">
                                {proposalReqs.submissionMethod.method}
                              </span>
                            </div>
                          )}
                          {proposalReqs.submissionMethod.address && (
                            <div>
                              <span className="font-medium text-blue-900">
                                Address:
                              </span>
                              <span className="ml-2 text-blue-800">
                                {proposalReqs.submissionMethod.address}
                              </span>
                            </div>
                          )}
                          {proposalReqs.submissionMethod.deadline && (
                            <div>
                              <span className="font-medium text-blue-900">
                                Deadline:
                              </span>
                              <span className="ml-2 text-blue-800 font-bold">
                                {proposalReqs.submissionMethod.deadline}
                              </span>
                            </div>
                          )}
                          {proposalReqs.submissionMethod.other && (
                            <div className="mt-2 text-blue-800">
                              {proposalReqs.submissionMethod.other}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Convert to Proposal */}
          {isAdmin() &&
            researchData.status === "bid" &&
            !researchData.converted_to_proposal_id && (
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg border border-primary-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Convert to Proposal
                </h3>
                <p className="text-slate-700 mb-4">
                  This RFP has been recommended for bidding. Convert it to a
                  full proposal to begin development.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select users to grant access:
                  </label>
                  <select
                    multiple
                    value={selectedUsers}
                    onChange={(e) =>
                      setSelectedUsers(
                        Array.from(e.target.selectedOptions, (option) =>
                          parseInt(option.value)
                        )
                      )
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    size="5"
                  >
                    {users.map((user) => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.username} ({user.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Hold Ctrl/Cmd to select multiple users
                  </p>
                </div>

                <button
                  onClick={handleConvertToProposal}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <FontAwesomeIcon icon="arrow-right" className="mr-2" />
                  Convert to Proposal
                </button>
              </div>
            )}

          {/* RFP Documents */}
          {research.documents && research.documents.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                <FontAwesomeIcon
                  icon="file-pdf"
                  className="mr-2 text-red-500"
                />
                RFP Documents
              </h2>
              <div className="space-y-2">
                {research.documents.map((doc) => (
                  <button
                    key={doc.document_id}
                    onClick={() => apiClient.viewDocument(doc.document_id)}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon
                        icon={
                          doc.file_extension === ".pdf"
                            ? "file-pdf"
                            : "file-word"
                        }
                        className={
                          doc.file_extension === ".pdf"
                            ? "text-red-500"
                            : "text-blue-500"
                        }
                      />
                      <div className="text-left">
                        <div className="font-medium text-slate-900">
                          {doc.original_file_name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {doc.classification && (
                            <span className="mr-3 capitalize">
                              {doc.classification.replace("_", " ")}
                            </span>
                          )}
                          {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                    </div>
                    <FontAwesomeIcon icon="eye" className="text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Convert to Proposal Modal */}
      <ConvertToProposalModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onConfirm={handleConvertToProposal}
        researchData={researchData}
        users={users}
        selectedUsers={selectedUsers}
        onUserToggle={handleUserToggle}
      />
    </Layout>
  );
};

export default RFPAnalysisDetail;
